/**
 * URL Manager
 * 
 * Central manager for all URL hash micro-apps:
 * - Splash screen animation
 * - Clock mode
 * - Crypto ticker mode
 * - Game mode
 * - Kaleidoscope mode
 * - Speech mode (displays user's speech transcript in URL)
 * 
 * Handles mode switching, state management, and URL updates.
 */

import {
  createSplashScreenState,
  generateSplashScreen,
  type SplashScreenState,
} from './splash-screen.js';
import {
  createTickerState,
  fetchCryptoPrices,
  generateTickerString,
  type TickerState,
} from './ticker.js';
import { generateClockString } from './clock.js';
import {
  initializeGame,
  generateGameString,
  type GameState,
} from './game.js';
import {
  initializeKaleidoscope,
  cycleKaleidoscope,
  generateKaleidoscopeString,
  type KaleidoscopeState,
} from './kaleidoscope.js';
import {
  createSpeechState,
  initializeSpeech,
  clearSpeech,
  cleanupSpeech,
  generateSpeechString,
  type SpeechState,
} from './speech.js';
import { bus } from '../../core/events.js';

export type UrlMode = 'clock' | 'ticker' | 'game' | 'kaleidoscope' | 'speech' | 'workshop';

export interface UrlManagerState {
  splashScreen: SplashScreenState;
  ticker: TickerState;
  game: GameState | null;
  kaleidoscope: KaleidoscopeState | null;
  speech: SpeechState;
  modeOverride: UrlMode | null;
  lastSecond: number;
  lastTickerUpdate: number;
  lastGameUpdate: number;
  lastSpeechUpdate: number;
}

/**
 * URL Manager class
 */
export class UrlManager {
  private state: UrlManagerState;

  constructor() {
    // Check for workshop mode query param
    const urlParams = new URLSearchParams(window.location.search);
    const initialModeOverride = urlParams.get('mode') === 'workshop' ? 'workshop' : null;

    this.state = {
      splashScreen: createSplashScreenState(),
      ticker: createTickerState(),
      game: null,
      kaleidoscope: null,
      speech: createSpeechState(),
      modeOverride: initialModeOverride,
      lastSecond: -1,
      lastTickerUpdate: 0,
      lastGameUpdate: 0,
      lastSpeechUpdate: 0,
    };

    // Initialize speech mode (listens to microphone events)
    // Pass update callback to trigger URL updates during typing animation
    initializeSpeech(this.state.speech, () => {
      // Always update when typing animation is active (microphone hijacks mode)
      this.updateSpeechUrl();
    });

    // Waveform visualization disabled - no longer listening for waveform updates
    // (was causing URL encoding issues with %20)

    // Start dedicated interval for URL updates during splash screen
    this.startSplashUrlUpdates();
  }

  /**
   * Get speech state (for checking AI activity)
   */
  getSpeechState(): SpeechState {
    return this.state.speech;
  }

  /**
   * Get current URL mode based on time or override
   */
  getMode(): UrlMode {
    // Hijack: If microphone is active, force speech mode (highest priority)
    if (this.state.speech.isListening) {
      return 'speech';
    }

    // Check for manual override
    if (this.state.modeOverride !== null) {
      return this.state.modeOverride;
    }

    // Normal time-based cycling (20-minute cycle with 4 modes + speech)
    const now = new Date();
    const minutes = now.getMinutes();
    const remainder = minutes % 20;

    if (remainder >= 0 && remainder <= 3) {
      return 'clock';
    } else if (remainder >= 4 && remainder <= 7) {
      return 'ticker';
    } else if (remainder >= 8 && remainder <= 11) {
      return 'game';
    } else if (remainder >= 12 && remainder <= 15) {
      return 'kaleidoscope';
    } else {
      return 'speech';
    }
  }

  /**
   * Set mode override (for manual mode selection)
   */
  setModeOverride(mode: UrlMode | null): void {
    this.state.modeOverride = mode;
  }

  /**
   * Check if in a specific mode
   */
  isClockMode(): boolean {
    return this.getMode() === 'clock';
  }

  isTickerMode(): boolean {
    return this.getMode() === 'ticker';
  }

  isGameMode(): boolean {
    return this.getMode() === 'game';
  }

  isKaleidoscopeMode(): boolean {
    return this.getMode() === 'kaleidoscope';
  }

  isSpeechMode(): boolean {
    return this.getMode() === 'speech';
  }

  isWorkshopMode(): boolean {
    return this.getMode() === 'workshop';
  }

  /**
   * Clear speech transcript (for manual clearing)
   */
  clearSpeech(): void {
    clearSpeech(this.state.speech);
    this.updateSpeechUrl();
  }

  /**
   * Update speech URL hash
   */
  private updateSpeechUrl(): void {
    const speechString = generateSpeechString(this.state.speech);
    history.replaceState(null, '', `#${speechString}`);
  }

  /**
   * Get game state (for game movement handling)
   */
  getGameState(): GameState | null {
    return this.state.game;
  }

  /**
   * Handle game movement (called from app.ts on keyboard input)
   */
  handleGameMovement(forward: boolean, up: boolean, down: boolean): void {
    if (!this.state.game) return;

    const game = this.state.game;

    if (game.level === 1) {
      // Level 1: Horizontal movement only
      const newPosition = forward
        ? Math.min(game.playerPosition + 1, game.levelLength - 1)
        : Math.max(game.playerPosition - 1, 0);

      if (newPosition !== game.playerPosition) {
        game.playerPosition = newPosition;

        // Check if player collected a coin
        if (game.coins.has(newPosition)) {
          game.coins.delete(newPosition);
          game.score++;
        }

        // Update URL immediately
        this.updateGameUrl();
      }
    } else {
      // Level 2: Flappy Bird style - vertical movement and horizontal progression
      let moved = false;

      // Handle vertical movement (W/S)
      if (up) {
        const newVertical = Math.max(game.playerVertical - 1, 0);
        if (newVertical !== game.playerVertical) {
          game.playerVertical = newVertical;
          moved = true;
        }
      } else if (down) {
        const newVertical = Math.min(game.playerVertical + 1, game.levelHeight - 1);
        if (newVertical !== game.playerVertical) {
          game.playerVertical = newVertical;
          moved = true;
        }
      }

      // Auto-advance horizontally (like Flappy Bird)
      if (forward || !moved) {
        const newPosition = Math.min(game.playerPosition + 1, game.levelLength - 1);
        if (newPosition !== game.playerPosition) {
          game.playerPosition = newPosition;
          moved = true;

          // Check for collision with obstacles
          const hitObstacle = game.obstacles.some(
            obs => obs.x === newPosition && obs.y === game.playerVertical
          );

          if (!hitObstacle) {
            // No collision - increase score
            game.score++;
          } else {
            // Collision - reset game
            this.state.game = null;
            this.initializeGame();
          }
        }
      }

      if (moved) {
        this.updateGameUrl();
      }
    }
  }

  /**
   * Cycle kaleidoscope pattern (called from app.ts on keyboard input)
   */
  cycleKaleidoscope(): void {
    if (!this.state.kaleidoscope) {
      this.initializeKaleidoscope();
      return;
    }
    cycleKaleidoscope(this.state.kaleidoscope);
    this.updateKaleidoscopeUrl();
  }

  /**
   * Initialize game state
   */
  private initializeGame(): void {
    if (this.state.game) return; // Already initialized
    this.state.game = initializeGame();
  }

  /**
   * Initialize kaleidoscope state
   */
  private initializeKaleidoscope(): void {
    if (this.state.kaleidoscope) return; // Already initialized
    this.state.kaleidoscope = initializeKaleidoscope();
  }

  /**
   * Update game URL hash
   */
  private updateGameUrl(): void {
    if (!this.state.game) return;
    const gameString = generateGameString(this.state.game);
    history.replaceState(null, '', `#${gameString}`);
  }

  /**
   * Update kaleidoscope URL hash
   */
  private updateKaleidoscopeUrl(): void {
    if (!this.state.kaleidoscope) return;
    const kaleidoscopeString = generateKaleidoscopeString(this.state.kaleidoscope);
    history.replaceState(null, '', `#${kaleidoscopeString}`);
  }

  /**
   * Start dedicated interval for URL updates during splash screen
   */
  private startSplashUrlUpdates(): void {
    if (this.state.splashScreen.urlUpdateIntervalId) return;

    // Update URL at 15fps (66ms) to avoid browser throttling
    this.state.splashScreen.urlUpdateIntervalId = setInterval(() => {
      if (!this.state.splashScreen.active) {
        this.stopSplashUrlUpdates();
        return;
      }

      const splashString = generateSplashScreen(this.state.splashScreen);
      if (splashString) {
        history.replaceState(null, '', `#${splashString}`);
      } else {
        // Splash screen finished
        this.stopSplashUrlUpdates();
      }
    }, 66); // ~15fps
  }

  /**
   * Stop dedicated interval for URL updates
   */
  private stopSplashUrlUpdates(): void {
    if (this.state.splashScreen.urlUpdateIntervalId) {
      clearInterval(this.state.splashScreen.urlUpdateIntervalId);
      this.state.splashScreen.urlUpdateIntervalId = null;
    }
  }

  /**
   * Update URL hash based on current mode
   * Called every animation frame from app.ts
   */
  update(): void {
    // Check for splash screen first
    if (this.state.splashScreen.active) {
      // Generate splash screen string (this updates document.title every frame)
      // URL updates are handled by dedicated interval (startSplashUrlUpdates)
      const splashString = generateSplashScreen(this.state.splashScreen);

      if (!splashString) {
        // Splash screen finished - transition to normal mode
        this.state.splashScreen.active = false;
        this.stopSplashUrlUpdates();
        document.title = 'pivot.is';
        // Continue to normal URL updates below
      } else {
        // Only update title here (URL updates are handled by interval)
        // Title updates every frame for smooth visual feedback
        return;
      }
    }

    const now = new Date();
    const currentSecond = now.getSeconds();
    const currentTime = Date.now();
    const mode = this.getMode();

    // Update tab title for speech mode (marquee display)
    // This makes the marquee text visible in the browser tab when it's not focused
    if (mode === 'speech') {
      const speechString = generateSpeechString(this.state.speech);
      if (speechString) {
        // Show marquee text in tab title so it's visible when tab is not focused
        document.title = speechString;
      } else {
        document.title = 'pivot.is';
      }
    } else if (mode !== 'speech' && document.title !== 'pivot.is') {
      // Reset title when not in speech mode (unless it's already 'pivot.is')
      // This prevents title from staying as speech text when switching modes
      document.title = 'pivot.is';
    }

    if (mode === 'ticker') {
      // Ticker mode: update every 500ms for slower scrolling marquee effect
      if (currentTime - this.state.lastTickerUpdate >= 500) {
        this.state.lastTickerUpdate = currentTime;

        // Fetch prices asynchronously (don't block)
        fetchCryptoPrices(this.state.ticker).catch(() => {
          // Silently handle errors - will use cached prices
        });

        const tickerString = generateTickerString(this.state.ticker);
        history.replaceState(null, '', `#${tickerString}`);
        this.state.lastSecond = currentSecond; // Update to prevent clock mode from triggering
      }
    } else if (mode === 'game') {
      // Game mode: initialize if needed, then update
      if (!this.state.game) {
        this.initializeGame();
      }

      // Update game URL periodically (every 100ms for smooth feel)
      if (currentTime - this.state.lastGameUpdate >= 100) {
        this.state.lastGameUpdate = currentTime;
        this.updateGameUrl();
      }
    } else if (mode === 'kaleidoscope') {
      // Kaleidoscope mode: initialize if needed, then update
      if (!this.state.kaleidoscope) {
        this.initializeKaleidoscope();
        this.updateKaleidoscopeUrl();
      }
      // URL updates happen on keypress (cycleKaleidoscope)
    } else if (mode === 'speech') {
      // Speech mode: update when typing animation is active
      // Waveform visualization disabled - only update when typing/untyping
      // Update less frequently when idle (every 1000ms)
      const speechUpdateInterval = 1000;
      if (currentTime - this.state.lastSpeechUpdate >= speechUpdateInterval) {
        this.state.lastSpeechUpdate = currentTime;
        // Only update if typing animation is active (will be triggered by animation callback)
        // Otherwise, URL will be empty when idle
        this.updateSpeechUrl();
      }
    } else {
      // Clock mode: only update when second changes
      if (currentSecond !== this.state.lastSecond) {
        this.state.lastSecond = currentSecond;
        const clockString = generateClockString();
        history.replaceState(null, '', `#${clockString}`);
      }
    }

    // Reset game state when leaving game mode
    if (mode !== 'game' && this.state.game) {
      this.state.game = null;
    }

    // Reset kaleidoscope state when leaving kaleidoscope mode
    if (mode !== 'kaleidoscope' && this.state.kaleidoscope) {
      this.state.kaleidoscope = null;
    }

    // Speech mode state persists (doesn't reset when leaving mode)
    // This allows the transcript to remain visible when not in speech mode
  }

  /**
   * Cleanup when URL manager is destroyed
   */
  destroy(): void {
    this.stopSplashUrlUpdates();
    cleanupSpeech(this.state.speech);
  }
}

